import type { ParticipantEditFieldState } from '../../selectors'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { DropdownInput } from '@/scopes/common/components/FormElements/DropdownInput'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { getPostalCodeExample } from '@/scopes/common/components/FormElements/utils/postalCodePatternUtils'
import { fieldProps, postalCodePattern } from '../../utils/participantEdit/participantEditFields'
import styles from './ParticipantEditShared.module.css'

interface ParticipantEditAddressSectionProps {
  fields: {
    street: ParticipantEditFieldState | undefined
    houseNumber: ParticipantEditFieldState | undefined
    houseNumberAddition: ParticipantEditFieldState | undefined
    postalCode: ParticipantEditFieldState | undefined
    city: ParticipantEditFieldState | undefined
    countryCode: ParticipantEditFieldState | undefined
  }
}

export function ParticipantEditAddressSection({ fields }: ParticipantEditAddressSectionProps) {
  const { formatMessage } = useIntl()
  const { street, houseNumber, houseNumberAddition, postalCode, city, countryCode } = fields
  const selectedCountryCode: string = useWatch({ name: 'countryCode' }) ?? ''

  if (!street && !houseNumber && !houseNumberAddition && !postalCode && !city && !countryCode) {
    return null
  }

  return (
    <div className={styles.section}>
      <Typography as="h2" variant="display-h-regular">
        {formatMessage({ id: 'common_address' })}
      </Typography>
      <div className={styles.fieldsStack}>
        {street && (
          <TextInput
            name="street"
            label={formatMessage({ id: 'booking_details_participant_edit_street_label' })}
            {...fieldProps(street, formatMessage)}
            pattern={street.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_street_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_street_required_error' })}
          />
        )}
        {houseNumber && (
          <TextInput
            name="houseNumber"
            label={formatMessage({ id: 'booking_details_participant_edit_street_number_label' })}
            {...fieldProps(houseNumber, formatMessage)}
            pattern={houseNumber.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_street_number_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_street_number_required_error' })}
          />
        )}
        {houseNumberAddition && (
          <TextInput
            name="houseNumberAddition"
            label={formatMessage({ id: 'booking_details_participant_edit_street_number_addition_label' })}
            {...fieldProps(houseNumberAddition, formatMessage)}
            pattern={houseNumberAddition.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_street_number_addition_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_street_number_addition_required_error' })}
          />
        )}
        {postalCode && (
          <TextInput
            name="postalCode"
            label={formatMessage({ id: 'booking_details_participant_edit_postal_code_label' })}
            {...fieldProps(postalCode, formatMessage)}
            pattern={postalCodePattern(postalCode, selectedCountryCode)}
            patternErrorMessage={formatMessage(
              { id: 'booking_details_participant_edit_postal_code_pattern_error' },
              { postalCodeExample: getPostalCodeExample(selectedCountryCode) },
            )}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_postal_code_required_error' })}
          />
        )}
        {city && (
          <TextInput
            name="city"
            label={formatMessage({ id: 'booking_details_participant_edit_city_label' })}
            {...fieldProps(city, formatMessage)}
            pattern={city.pattern}
            patternErrorMessage={formatMessage({ id: 'booking_details_participant_edit_city_pattern_error' })}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_city_required_error' })}
          />
        )}
        {countryCode && (
          <DropdownInput
            name="countryCode"
            items={countryCode.options}
            label={formatMessage({ id: 'booking_details_participant_edit_country_label' })}
            {...fieldProps(countryCode, formatMessage)}
            requiredErrorMessage={formatMessage({ id: 'booking_details_participant_edit_country_required_error' })}
          />
        )}
      </div>
    </div>
  )
}
